<template>
    <div class="max-w-2xl mx-auto px-4 py-6 sm:px-6 lg:px-8 space-y-6">
        <div class="flex items-center justify-between">
            <button @click="goBack" class="btn btn-secondary !p-2 !rounded-full">
                <span class="material-symbols-outlined">arrow_back</span>
            </button>
            <h1 class="text-xl font-bold text-slate-800 dark:text-slate-100">
                {{ isEditing ? 'Editar Investimento' : 'Adicionar Investimento' }}
            </h1>
            <button @click="handleSubmit" :disabled="isSubmitting" class="btn btn-primary min-w-[80px]">
                <span v-if="isSubmitting" class="material-symbols-outlined animate-spin text-lg">progress_activity</span>
                <span v-else>Salvar</span>
            </button>
        </div>

        <div class="panel p-5 sm:p-6">
            <form @submit.prevent="handleSubmit" class="space-y-6">
                <div>
                    <label for="invDescricao" class="form-label">Descrição</label>
                    <input id="invDescricao" v-model="investimento.descricao" type="text" required
                        placeholder="Ex: Ações Petrobras" class="form-input" />
                </div>
                <div>
                    <label for="invTipo" class="form-label">Tipo</label>
                    <select id="invTipo" v-model="investimento.tipo" required class="form-select">
                        <option disabled value="">Selecione um tipo</option>
                        <option v-for="tipo in tiposInvestimento" :key="tipo.value" :value="tipo.value">{{ tipo.label }}</option>
                    </select>
                </div>
                <div v-if="showTickerInput" class="transition-all duration-300">
                    <label for="invTicker" class="form-label">Ticker</label>
                    <input id="invTicker" v-model="investimento.ticker" type="text" placeholder="Ex: PETR4"
                        class="form-input uppercase" />
                </div>
                <div>
                    <label for="invInstituicao" class="form-label">Instituição / Corretora</label>
                    <input id="invInstituicao" v-model="investimento.instituicao" type="text"
                        placeholder="Ex: XP Investimentos" class="form-input" />
                </div>
                <div>
                    <label for="invValor" class="form-label">Valor Atual (R$)</label>
                    <input id="invValor" v-model.number="investimento.valorAtual" type="number" step="0.01" required
                        placeholder="0.00" class="form-input" />
                </div>
            </form>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import axios from 'axios';

const router = useRouter();
const route = useRoute();

const isEditing = computed(() => !!route.params.id);
const isSubmitting = ref(false);

const getInitialFormData = () => ({
    id: null, descricao: '', tipo: 'RENDA_FIXA', instituicao: '', ticker: '', valorAtual: null
});
const investimento = ref(getInitialFormData());

const apiUrl = 'https://backend.controlease.com.br/investimentos';
const authConfig = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const tiposInvestimento = ref([
    { value: 'RENDA_FIXA', label: 'Renda Fixa' },
    { value: 'RENDA_VARIAVEL', label: 'Renda Variável (Ações, BDRs)' },
    { value: 'FUNDO_IMOBILIARIO', label: 'Fundo Imobiliário (FII)' },
    { value: 'TESOURO_DIRETO', label: 'Tesouro Direto' },
    { value: 'CRIPTOATIVO', label: 'Criptoativo' },
]);

const showTickerInput = computed(() => {
    if (!investimento.value) return false;
    const tickerTypes = ['RENDA_VARIAVEL', 'FUNDO_IMOBILIARIO'];
    return tickerTypes.includes(investimento.value.tipo);
});

watch(investimento, (newVal) => {
    if (newVal && !showTickerInput.value) { newVal.ticker = ''; }
}, { deep: true });

const fetchInvestimento = async () => {
    if (!isEditing.value) return;
    try {
        const response = await axios.get(`${apiUrl}/${route.params.id}`, authConfig());
        investimento.value = response.data;
    } catch {
        alert('Erro ao carregar dados do investimento.');
        router.push('/user/investimentos');
    }
};

const handleSubmit = async () => {
    if (isSubmitting.value) return;
    isSubmitting.value = true;
    try {
        const payload = { ...investimento.value, ticker: investimento.value.ticker?.toUpperCase() };
        if (isEditing.value) {
            await axios.put(`${apiUrl}/${payload.id}`, payload, authConfig());
        } else {
            await axios.post(apiUrl, payload, authConfig());
        }
        router.push('/user/investimentos');
    } catch (error) {
        console.error(`Erro ao salvar investimento:`, error.response?.data || error.message);
        alert(error.response?.data?.message || 'Erro ao salvar investimento.');
    } finally {
        isSubmitting.value = false;
    }
};

const goBack = () => {
    router.push('/user/investimentos'); 
};

onMounted(() => {
    if (isEditing.value) {
        fetchInvestimento();
    } else {
        investimento.value = getInitialFormData(); 
    }
});
</script>

<style scoped>
.panel { @apply bg-white/70 dark:bg-brand-800/60 backdrop-blur-lg ring-1 ring-black/5 dark:ring-white/10 rounded-2xl shadow-xl shadow-slate-200/30 dark:shadow-black/20; }
.btn { @apply inline-flex items-center justify-center font-semibold py-2.5 px-5 rounded-xl transition-all duration-200 text-sm active:scale-95 disabled:opacity-60; }
.btn-primary { @apply btn bg-brand-600 hover:bg-brand-700 text-white shadow-lg shadow-brand-500/20; }
.btn-secondary { @apply btn bg-white/70 dark:bg-slate-800/50 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 ring-1 ring-black/5 dark:ring-white/10 shadow-sm; }
.form-input, .form-select { @apply w-full px-3.5 py-2.5 text-sm rounded-xl bg-white dark:bg-slate-800/50 border border-slate-300 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500; }
.form-select { @apply appearance-none; }
.form-label { @apply block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5; }
.animate-spin { animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
</style>